import { Controller } from '@hotwired/stimulus'

// Responsibilities:
//   - Desktop (>=768px): collapse/expand the sidebar between 288px and a 76px icon rail,
//     persisting the choice to a cookie (read server-side by AdminHelper#admin_sidebar_collapsed?
//     so the correct markup renders on the very first response — no client-side correction,
//     so no flash of the wrong state on load/refresh; this controller only needs to handle
//     the *interactive* toggle from then on). The chevron swaps direction (no animation),
//     labels hide, nav items center, and collapsed items get a native tooltip.
//   - Mobile (<768px): open/close the drawer overlay + backdrop.
//   - Re-assert the correct mode when the viewport crosses the md breakpoint.

const COOKIE_NAME = 'crg_admin_sidebar_collapsed'
const EXPANDED_W = '18rem' // 288px (Tailwind w-72)
const RAIL_W = '68px' // w-17

function readCookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : null
}

function writeCookie(name, value) {
  document.cookie = `${name}=${value}; path=/; max-age=31536000; SameSite=Lax`
}

// Applies a style change to `el` with its CSS transition temporarily disabled, so the
// change is instant regardless of any transition-* class it carries. Used to re-assert
// state (on connect, or when crossing the md breakpoint) without animating — only an
// actual interactive toggle should animate.
function setInstant(el, apply) {
  const prevTransition = el.style.transition
  el.style.transition = 'none'
  apply()
  el.offsetHeight // force layout so the disabled transition actually takes effect
  el.style.transition = prevTransition
}

export default class extends Controller {
  static targets = [
    'sidebar', // desktop <aside>
    'main', // breadcrumbs nav + <main> content — margin-left kept in sync since the aside is fixed/out of flow
    'header', // desktop header row (gap removed when collapsed, see applyDesktop)
    'brand', // desktop wordmark (hidden when collapsed)
    'chevron', // desktop collapse chevron <svg>
    'collapseBtn',
    'label', // desktop nav label <span>s (hidden when collapsed)
    'navItem', // desktop nav links (get title tooltip when collapsed)
    'navGroup', // desktop <ul>/<div> wrapping the nav items (stop stretching them when collapsed)
    'drawer', // mobile drawer <aside>
    'backdrop' // mobile backdrop
  ]

  connect() {
    this.mql = window.matchMedia('(min-width: 768px)')
    this._onMedia = () => this.applyMode()
    this.mql.addEventListener('change', this._onMedia)

    this.collapsed = readCookie(COOKIE_NAME) === '1'
    this.mobileOpen = false
    this.applyMode()
  }

  disconnect() {
    this.mql.removeEventListener('change', this._onMedia)
  }

  get isDesktop() {
    return this.mql.matches
  }

  // --- Actions -------------------------------------------------------------

  // Desktop chevron. (On mobile the top-bar button uses openMobile directly.)
  toggle() {
    if (this.isDesktop) {
      this.collapsed = !this.collapsed
      writeCookie(COOKIE_NAME, this.collapsed ? '1' : '0')
      this.applyDesktop() // animated — the actual user-initiated collapse/expand
    } else {
      this.mobileOpen ? this.closeMobile() : this.openMobile()
    }
  }

  openMobile() {
    this.mobileOpen = true
    this.drawerTarget.classList.remove('-translate-x-full')
    this.drawerTarget.classList.add('translate-x-0')
    this.backdropTarget.classList.remove('opacity-0', 'pointer-events-none')
    this.backdropTarget.classList.add('opacity-100')
    document.addEventListener('keydown', this._onKeydown)
  }

  closeMobile() {
    this.mobileOpen = false
    this.drawerTarget.classList.add('-translate-x-full')
    this.drawerTarget.classList.remove('translate-x-0')
    this.backdropTarget.classList.add('opacity-0', 'pointer-events-none')
    this.backdropTarget.classList.remove('opacity-100')
    document.removeEventListener('keydown', this._onKeydown)
  }

  // --- Internal ------------------------------------------------------------

  // Called on connect and whenever the viewport crosses the md breakpoint — both cases
  // just re-assert the correct state (nothing the user actively toggled), and the
  // sidebar's hidden/flex display flips instantly at that same breakpoint, so this must
  // apply instantly too or it visibly falls out of sync with it (see applyDesktop).
  applyMode() {
    if (this.isDesktop) this.closeMobile()
    this.applyDesktop({ instant: true })
  }

  applyDesktop({ instant = false } = {}) {
    const width = this.collapsed ? RAIL_W : EXPANDED_W

    // Sidebar width. The aside is fixed (out of flow), so breadcrumbs/main need their
    // margin-left kept in sync manually. Both carry a transition so an interactive
    // toggle animates them together with the sidebar's own width — but that same
    // transition would also animate the md:ml-* class's atomic switch at the exact
    // breakpoint where the sidebar's hidden/flex display flips instantly (which can't
    // be transitioned), visibly desyncing the two. `instant` disables the transition
    // just for that re-assertion, without removing it for the real toggle.
    const setWidth = () => {
      if (this.hasSidebarTarget) this.sidebarTarget.style.width = width
    }
    const setMargin = (el) => {
      el.style.marginLeft = this.isDesktop ? width : ''
    }
    if (instant) {
      if (this.hasSidebarTarget) setInstant(this.sidebarTarget, setWidth)
      this.mainTargets.forEach((el) => setInstant(el, () => setMargin(el)))
    } else {
      setWidth()
      this.mainTargets.forEach(setMargin)
    }

    // Header: fade the wordmark out when collapsed (opacity, not display, so it animates
    // instead of vanishing instantly). Because it stays in the layout to do that (unlike
    // display:none), it still counts as a flex item — so the header's gap-3 would keep
    // reserving 12px next to the collapse button even with brand invisible, overflowing
    // the rail and clipping the button's own right-side padding. Collapse the gap to 0 to
    // match. The collapse button stays right-anchored (ml-auto, always) and nav icons stay
    // left-anchored (their default position) in both states, so nothing shifts horizontally
    // while the sidebar's width is transitioning.
    if (this.hasBrandTarget) {
      this.brandTarget.classList.toggle('opacity-0', this.collapsed)
      this.brandTarget.setAttribute(
        'aria-hidden',
        this.collapsed ? 'true' : 'false'
      )
    }
    if (this.hasHeaderTarget) {
      this.headerTarget.classList.toggle('gap-3', !this.collapsed)
      this.headerTarget.classList.toggle('gap-0', this.collapsed)
    }
    if (this.hasCollapseBtnTarget) {
      this.collapseBtnTarget.setAttribute(
        'title',
        this.collapsed ? 'Expand sidebar' : 'Collapse sidebar'
      )
    }
    if (this.hasChevronTarget) {
      // Swap direction — no transition, per design.
      this.chevronTarget.classList.toggle('rotate-180', this.collapsed)
    }

    // Nav groups (the <ul> and the bottom <div>) default to align-items: stretch, which
    // forces each <li>/wrapper to the group's full width regardless of the nav item's own
    // width. Switch to items-start when collapsed so they shrink to fit the w-11 button.
    this.navGroupTargets.forEach((el) =>
      el.classList.toggle('items-start', this.collapsed)
    )

    // Nav items: fade labels out (opacity, not display — same reasoning as the wordmark
    // above) + tooltip when collapsed. The row itself shrinks to a snug, icon-sized box
    // (w-11) instead of staying full-width — otherwise the empty space where the label
    // used to be just sits there next to the icon. The icon is left-anchored in both
    // states, so it doesn't move; only the box past it shrinks. The nav/aside's own
    // overflow-hidden clips the label once it's past the shrunk box.
    this.labelTargets.forEach((el) => {
      el.classList.toggle('opacity-0', this.collapsed)
      el.setAttribute('aria-hidden', this.collapsed ? 'true' : 'false')
    })
    this.navItemTargets.forEach((el) => {
      el.classList.toggle('w-11', this.collapsed)
      const label = el.getAttribute('data-label') || el.textContent.trim()
      if (this.collapsed) el.setAttribute('title', label)
      else el.removeAttribute('title')
    })
  }

  _onKeydown = (e) => {
    if (e.key === 'Escape') this.closeMobile()
  }
}
